import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { NoopValueAccessorDirective } from '../facility-select/noop-value-accessor.directive';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContainerMaskItem, ContainerSelectorService } from './container-selector.service';
import {
  CheckboxComponent,
  TableColumnItem,
  TableColumnTypeEnum,
  TableComponent,
} from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'mon-container-selector',
  templateUrl: './container-selector.component.html',
  styleUrl: './container-selector.component.scss',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    FormsModule,
    CheckboxComponent,
    TranslateModule,
    TableComponent,
  ],
  hostDirectives: [NoopValueAccessorDirective],
  providers: [ContainerSelectorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContainerSelectorComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly containerSelectorService = inject(ContainerSelectorService);
  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  private readonly facilityId$ = new BehaviorSubject<string | null>(null);
  private readonly keyword$ = new BehaviorSubject<string>('');

  @Input()
  public set facilityId(value: string | undefined) {
    this.facilityId$.next(value ?? null);
  }

  public get facilityId(): string | undefined {
    const value = this.facilityId$.value;
    return value ?? undefined;
  }

  @Input()
  public set keyword(value: string | null | undefined) {
    this.keyword$.next(value ?? '');
  }

  @Output()
  public readonly selectionChange = new EventEmitter<IDictionaryItem<string>[]>();

  private loadedCount = 0;
  private readonly containersMap = new Map<string, ContainerMaskItem>();

  @ViewChild('tableHost', { read: ElementRef })
  private readonly tableHost?: ElementRef<HTMLElement>;

  protected readonly CONTAINER_COLUMNS: TableColumnItem[] = [
    {
      propName: 'name',
      captionTranslateKey: 'monitoring.scanner.new-scan.container',
      type: TableColumnTypeEnum.Text,
    },
    {
      propName: 'mask',
      captionTranslateKey: 'monitoring.scanner.new-scan.ip-range',
      type: TableColumnTypeEnum.Text,
    },
  ];

  protected readonly isLoading = this.containerSelectorService.isLoading;
  protected readonly hasLoaded = this.containerSelectorService.hasLoaded;

  public asContainerMaskItem = (row: unknown): ContainerMaskItem => row as ContainerMaskItem;

  public get control(): FormControl<string[] | null> {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-container-selector must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-container-selector supports only FormControl');
    }

    return control as FormControl<string[] | null>;
  }

  protected readonly containers$ = combineLatest([
    this.facilityId$.pipe(distinctUntilChanged()),
    this.keyword$.pipe(
      map((value: string): string => value.trim()),
      debounceTime(250),
      distinctUntilChanged(),
    ),
  ]).pipe(
    tap((): void => this.resetLocalState()),
    switchMap(([facilityId, keyword]) =>
      this.containerSelectorService.source$(facilityId, keyword || null).pipe(
        scan(
          (acc: ContainerMaskItem[], newbies: ContainerMaskItem[]): ContainerMaskItem[] =>
            this.mergeContainers(acc, newbies),
          [],
        ),
        tap((items: ContainerMaskItem[]): void => {
          this.loadedCount = items.length;

          this.containersMap.clear();
          items.forEach((item): void => {
            this.containersMap.set(item.id, item);
          });
        }),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    combineLatest([this.control.valueChanges.pipe(startWith(this.control.value)), this.containers$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([ids]): void => {
        const selectedIds = ids ?? [];

        this.selectionChange.emit(
          selectedIds.map(
            (id: string): IDictionaryItem<string> => ({
              id,
              name: this.containersMap.get(id)?.name ?? '-',
            }),
          ),
        );
      });
  }

  public ngAfterViewInit(): void {
    const tableContainer = this.tableHost?.nativeElement.querySelector('.table-container');

    if (!tableContainer) {
      return;
    }

    fromEvent(tableContainer, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const target = event.target as HTMLElement;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (distanceToBottom < 64) {
          this.loadMore();
        }
      });
  }

  public toggleContainer(checked: boolean, containerId: string): void {
    const selected = new Set<string>(this.control.value ?? []);

    if (checked) {
      selected.add(containerId);
    } else {
      selected.delete(containerId);
    }

    this.updateSelection(selected);
  }

  public toggleAllVisible(checked: boolean, containers: ContainerMaskItem[]): void {
    const selected = new Set<string>(this.control.value ?? []);

    if (checked) {
      containers.forEach((container) => selected.add(container.id));
    } else {
      containers.forEach((container) => selected.delete(container.id));
    }

    this.updateSelection(selected);
  }

  public isContainerSelected(containerId: string): boolean {
    return (this.control.value ?? []).includes(containerId);
  }

  public isAllVisibleSelected(containers: ContainerMaskItem[]): boolean {
    if (containers.length === 0) {
      return false;
    }

    const selected = new Set<string>(this.control.value ?? []);
    return containers.every((container) => selected.has(container.id));
  }

  public loadMore(): void {
    if (this.loadedCount < this.containerSelectorService.totalCount()) {
      this.containerSelectorService.loadMore();
    }
  }

  private updateSelection(selected: Set<string>): void {
    this.control.setValue(Array.from(selected));
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  private resetLocalState(): void {
    this.loadedCount = 0;
    this.containersMap.clear();
  }

  private mergeContainers(
    current: ContainerMaskItem[],
    incoming: ContainerMaskItem[],
  ): ContainerMaskItem[] {
    const itemsMap = new Map<string, ContainerMaskItem>();

    current.forEach((item) => itemsMap.set(item.id, item));
    incoming.forEach((item) => itemsMap.set(item.id, item));

    return Array.from(itemsMap.values());
  }
}
